"use client";

import * as React from "react";
import { BrainCircuit, Compass, Workflow, Target, Sigma, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
const philosophies = [{
  title: "Non-Partisan",
  description: "Operating outside of political influence to ensure objective economic reporting.",
  proof: "Our charter mandates structural separation from government funding cycles, ensuring that data releases remain immune to electoral pressures or policy agendas.",
  icon: Compass
}, {
  title: "Data-Driven",
  description: "Utilizing high-frequency transactional data for real-time household insights.",
  proof: "By bypassing lagging survey methods in favor of direct ledger analysis, we capture economic shifts as they happen, providing a more accurate pulse of the nation.",
  icon: Sigma
}, {
  title: "Publicly Verifiable",
  description: "Maintaining full transparency in our calculation models and data sources.",
  proof: "Open-source methodology allows independent economists to audit our indices, reinforcing the Bureau's role as a trusted source of truth for all Australians.",
  icon: Workflow
}, {
  title: "Institutional Rigor",
  description: "Applying academic-grade standards to every index and monthly commentary.",
  proof: "Every data point undergoes triple-blind verification against secondary benchmarks to eliminate statistical anomalies before public dissemination.",
  icon: Target
}];
export default function EducationalPhilosophy() {
  return <section data-section-id="236845" id="institutional-independence" className="bg-background text-foreground py-24 px-6 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-none mb-4 border border-primary/20">
            <Sigma className="size-5 text-primary" />
          </div>
          <h2 className="text-3xl md:text-5xl font-default font-bold tracking-tight text-primary">
            The Pillar of Objective Truth
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-default">
            The Australian Index Bureau operates with absolute autonomy. Our methodology is built on four foundational pillars of institutional independence.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {philosophies.map((item, i) => <div key={i} data-index={i} className="h-full">
              <HoverCard openDelay={200} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <div className="h-full cursor-crosshair outline-none">
                    <Card className="h-full bg-card border-border rounded-none hover:border-primary/40 hover:bg-accent/5 transition-all duration-300 group shadow-sm hover:shadow-md">
                      <CardHeader className="pb-4">
                        <div className="size-12 rounded-none bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                          <item.icon className="size-6 text-primary group-hover:text-primary-foreground" />
                        </div>
                        <CardTitle className="font-default text-xl tracking-tight text-primary">
                          {item.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-muted-foreground text-base leading-relaxed font-default">
                          {item.description}
                        </CardDescription>
                        <div className="mt-8 text-xs font-mono text-muted-foreground flex items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                          <span>VERIFY PROTOCOL</span>
                          <ArrowRight className="size-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="bottom" align="start" className="w-80 bg-popover border-border rounded-none shadow-xl p-5">
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold font-default flex items-center gap-2 text-foreground">
                      <span className="bg-primary text-primary-foreground px-1.5 py-0.5 rounded-none text-[10px] font-mono uppercase tracking-wider">
                        Audit
                      </span>
                      Methodological Proof
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed font-default">
                      {item.proof}
                    </p>
                    <div className="pt-2 border-t border-border flex justify-end">
                      <span className="text-[10px] font-mono text-muted-foreground italic">
                        AIB-REF: 2024/IND
                      </span>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>)}
        </div>
      </div>
    </section>;
}